import React, { useState } from 'react';
import { UserAvatar } from './UserAvatar';
import { ImagePreviewModal } from './ImagePreviewModal';
import { getGenderInVietnamese } from '../utils/genderHelper';
import { calculateAge, formatBirthParts } from '../utils/dateHelper';
import { UserTreeNode as UserTreeNodeType } from '../utils/familyTreeHelper';

interface FamilyTreeNodeProps {
  node: UserTreeNodeType;
  hideChildren?: boolean;
  className?: string;
}

const renderMemberCard = (
  member: UserTreeNodeType,
  spouse?: UserTreeNodeType,
  onOpen?: (target: UserTreeNodeType) => void
) => {
  const openForMember = () => onOpen?.(member);
  const openForSpouse = () => onOpen?.(spouse ?? member);
  const memberAge = calculateAge(member.birthYear, member.diedYear, member.isAlive !== false);

  return (
    <div className="tree-node" onClick={openForMember}>
      {member.spouseName ? (
        <div className="tree-node__couple-avatars">
          <div className="tree-node__avatar-item" onClick={(event) => {
            event.stopPropagation();
            openForMember();
          }}>
            <UserAvatar imageUrl={member.imageUrl} name={member.fullName} size="md" isDeceased={member.isAlive === false} />
            <span className="tree-node__avatar-label">{member.fullName}</span>
          </div>

          <div className="tree-node__avatar-item" onClick={(event) => {
            event.stopPropagation();
            openForSpouse();
          }}>
            <UserAvatar
              imageUrl={member.spouseImageUrl}
              name={member.spouseName}
              size="md"
              isDeceased={member.spouseIsAlive === false}
            />
            <span className="tree-node__avatar-label">{member.spouseName}</span>
          </div>
        </div>
      ) : (
        <div className="tree-node__avatar-item tree-node__avatar-item--single" onClick={(event) => {
          event.stopPropagation();
          openForMember();
        }}>
          <UserAvatar imageUrl={member.imageUrl} name={member.fullName} size="md" isDeceased={member.isAlive === false} />
        </div>
      )}

      <p className="tree-node__name">{member.fullName}</p>
      {member.homeName && (
        <p className="tree-node__home-name">{member.homeName}</p>
      )}
      <span className="tree-node__meta">
        {getGenderInVietnamese(member.gender)} - đời thứ {member.generation ?? 1}
      </span>
      <span className="tree-node__dates">
        {member.birthYear || (member.isAlive === false && member.diedYear)
          ? `${member.birthYear ?? '?'}${member.isAlive === false && member.diedYear ? ` - ${member.diedYear}` : ''}`
          : formatBirthParts(member.birthDay, member.birthMonth, member.birthYear)}
        {memberAge !== null && ` (${member.isAlive === false ? 'thọ ' : ''}${memberAge} tuổi)`}
      </span>
    </div>
  );
};

export const FamilyTreeNode: React.FC<FamilyTreeNodeProps> = ({ node, hideChildren = false, className = '' }) => {
  const [previewState, setPreviewState] = useState<{ node: UserTreeNodeType; spouse?: UserTreeNodeType } | null>(null);
  const hasChildren = node.children.length > 0;
  const spouse = node.spouse;

  const openPreview = (member: UserTreeNodeType) => {
    setPreviewState({ node: member, spouse: spouse && member.id !== spouse.id ? spouse : undefined });
  };

  return (
    <>
      <li className={className}>
        {spouse && !hideChildren ? (
          renderMemberCard(node, spouse, openPreview)
        ) : (
          renderMemberCard(node, undefined, openPreview)
        )}

        {!hideChildren && hasChildren && (
          <ul>
            {node.children.map((child) => (
              <FamilyTreeNode key={child.id} node={child} />
            ))}
          </ul>
        )}
      </li>

      <ImagePreviewModal
        isOpen={Boolean(previewState)}
        imageUrl={previewState?.node.imageUrl}
        name={previewState?.node.fullName}
        secondaryImageUrl={previewState?.node?.spouseImageUrl}
        secondaryName={previewState?.node?.spouseName}
        onClose={() => setPreviewState(null)}
      />
    </>
  );
};
